<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { formatDuration, formatPhone } from 'dashboard/helper/relayVoicePhone';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  calls: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});
const emit = defineEmits(['redial']);
const { t } = useI18n();
const missedOnly = ref(false);

const shown = computed(() =>
  missedOnly.value
    ? props.calls.filter(call => call.outcome === 'missed')
    : props.calls
);
const missedCount = computed(
  () => props.calls.filter(call => call.outcome === 'missed').length
);
const name = call =>
  call.contact?.name ||
  (call.direction === 'local'
    ? t('RELAY_VOICE.CALLS.INTERNAL', { extension: call.number })
    : formatPhone(call.number) || t('RELAY_VOICE.UNKNOWN_CALLER'));
const icon = call => {
  if (call.outcome === 'missed') return 'i-ph-phone-x-bold';
  return call.direction === 'inbound'
    ? 'i-ph-phone-incoming-bold'
    : 'i-ph-phone-outgoing-bold';
};
const when = call =>
  call.started_at ? dynamicTime(Date.parse(call.started_at) / 1000) : '';
</script>

<template>
  <div class="flex flex-col gap-2 min-h-0">
    <div class="flex gap-1" role="group">
      <button
        v-for="option in [false, true]"
        :key="String(option)"
        type="button"
        :aria-pressed="missedOnly === option"
        class="px-2.5 py-1 rounded-full text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="
          missedOnly === option
            ? 'bg-n-slate-12 text-n-solid-1'
            : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
        "
        @click="missedOnly = option"
      >
        {{
          option ? t('RELAY_VOICE.MISSED_ONLY') : t('RELAY_VOICE.ALL_RECENT')
        }}
        <span v-if="option && missedCount" class="tabular-nums">
          {{ missedCount }}
        </span>
      </button>
    </div>
    <div v-if="loading && !calls.length" class="flex justify-center py-6">
      <Spinner />
    </div>
    <p
      v-else-if="!shown.length"
      class="text-sm text-n-slate-10 py-6 text-center"
    >
      {{ t('RELAY_VOICE.RECENT_EMPTY') }}
    </p>
    <ul v-else class="flex flex-col overflow-y-auto max-h-72 -mx-1">
      <li v-for="call in shown" :key="call.id">
        <button
          type="button"
          class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-start hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand group"
          :aria-label="t('RELAY_VOICE.REDIAL', { number: name(call) })"
          @click="emit('redial', call.number)"
        >
          <span
            class="flex items-center justify-center size-8 rounded-full shrink-0"
            :class="
              call.outcome === 'missed'
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            <Icon :icon="icon(call)" class="size-4" />
          </span>
          <span class="flex-1 min-w-0">
            <span
              class="block text-sm font-medium truncate"
              :class="
                call.outcome === 'missed' ? 'text-n-ruby-11' : 'text-n-slate-12'
              "
            >
              {{ name(call) }}
            </span>
            <span class="block text-xs text-n-slate-10 truncate">
              {{ t(`RELAY_VOICE.OUTCOMES.${call.outcome}`) }}
              <template v-if="call.talk_seconds">
                · {{ formatDuration(call.talk_seconds) }}
              </template>
            </span>
          </span>
          <span class="text-xs text-n-slate-10 shrink-0 group-hover:hidden">
            {{ when(call) }}
          </span>
          <Icon
            icon="i-ph-phone-bold"
            class="size-4 text-n-teal-10 shrink-0 hidden group-hover:block"
          />
        </button>
      </li>
    </ul>
  </div>
</template>
